import { recoveryCodeSchema, totpCodeSchema } from '@fluxus/shared';
import { ShieldCheck } from 'lucide-react';
import { useId, useRef, useState, type FormEvent } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { FormAlert, FormField, fieldA11y } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { ApiError, errorMessage } from '@/lib/api/errors';
import { useAuth } from '@/lib/auth/session';

type Props = {
  challengeToken: string;
  /** O desafio venceu ou foi recusado: volta para e-mail e senha, com o motivo. */
  onRestart: (reason?: unknown) => void;
};

/**
 * Segunda etapa do login (RF-03): código do app autenticador ou, se a pessoa estiver sem o celular,
 * um código de recuperação. Ao confirmar, a sessão abre e a tela de login redireciona.
 */
export function TwoFactorStep({ challengeToken, onRestart }: Props) {
  const { client } = useAuth();
  const [useRecovery, setUseRecovery] = useState(false);
  const [code, setCode] = useState('');
  const [fieldError, setFieldError] = useState<string>();
  const [formError, setFormError] = useState<unknown>(null);
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const ids = { code: useId(), alert: useId() };

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (submitting) return;
    setFormError(null);
    const parsed = (useRecovery ? recoveryCodeSchema : totpCodeSchema).safeParse(code);
    if (!parsed.success) {
      setFieldError(parsed.error.issues[0]?.message);
      inputRef.current?.focus();
      return;
    }
    setFieldError(undefined);
    setSubmitting(true);
    try {
      const { recoveryCodesRemaining } = await client.completeTwoFactor(
        challengeToken,
        useRecovery ? { recoveryCode: parsed.data } : { code: parsed.data },
      );
      if (recoveryCodesRemaining !== undefined) {
        toast.warning('Você entrou com um código de recuperação', {
          description:
            recoveryCodesRemaining === 0
              ? 'Não restam códigos. Gere novos em Minha conta › Verificação em duas etapas.'
              : `Restam ${recoveryCodesRemaining}. Se perdeu o celular, gere novos códigos em Minha conta.`,
          duration: 10_000,
        });
      }
    } catch (err) {
      if (err instanceof ApiError && err.code === 'CHALLENGE_EXPIRED') return onRestart(err);
      if (err instanceof ApiError && err.code === 'INVALID_CODE') {
        setFieldError(useRecovery ? 'Código de recuperação incorreto ou já usado.' : err.message);
      } else {
        setFormError(err);
      }
      setCode('');
      inputRef.current?.focus();
    } finally {
      setSubmitting(false);
    }
  }

  const locked = formError instanceof ApiError && formError.code === 'ACCOUNT_LOCKED';

  return (
    <>
      <span className="inline-flex size-12 items-center justify-center rounded-full bg-brand-soft text-brand-strong dark:text-brand">
        <ShieldCheck className="size-6" aria-hidden />
      </span>
      <h1 className="mt-5 text-3xl font-extrabold tracking-tight">Verificação em duas etapas</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        {useRecovery
          ? 'Digite um dos códigos de recuperação que você guardou ao ativar a verificação. Cada código vale uma vez.'
          : 'Abra o app autenticador no seu celular e digite o código de 6 números desta conta.'}
      </p>
      <form
        noValidate
        onSubmit={onSubmit}
        className="mt-8 flex flex-col gap-5"
        aria-describedby={formError ? ids.alert : undefined}
      >
        <FormField
          id={ids.code}
          label={useRecovery ? 'Código de recuperação' : 'Código do app autenticador'}
          error={fieldError}
        >
          <Input
            key={useRecovery ? 'recuperacao' : 'app'}
            ref={inputRef}
            {...fieldA11y(ids.code, fieldError)}
            autoFocus
            value={code}
            maxLength={useRecovery ? 20 : 7}
            inputMode={useRecovery ? 'text' : 'numeric'}
            autoComplete={useRecovery ? 'off' : 'one-time-code'}
            autoCapitalize="none"
            spellCheck={false}
            placeholder={useRecovery ? 'abcde-fghjk' : '123456'}
            onChange={(e) => {
              setCode(e.target.value);
              setFieldError(undefined);
            }}
            className="h-12 text-center font-mono text-lg tracking-[0.3em]"
          />
        </FormField>
        {formError ? (
          <FormAlert
            id={ids.alert}
            tone={locked ? 'locked' : 'error'}
            title={locked ? 'Acesso bloqueado temporariamente' : undefined}
          >
            {errorMessage(formError)}
          </FormAlert>
        ) : null}
        <Button type="submit" size="lg" className="w-full" loading={submitting}>
          Confirmar
        </Button>
      </form>
      <div className="mt-5 flex flex-col items-center gap-2 text-sm">
        <Button
          type="button"
          variant="link"
          onClick={() => {
            setUseRecovery((v) => !v);
            setCode('');
            setFieldError(undefined);
          }}
        >
          {useRecovery
            ? 'Usar o código do app autenticador'
            : 'Estou sem o celular: usar um código de recuperação'}
        </Button>
        <Button type="button" variant="ghost" onClick={() => onRestart()}>
          Voltar e entrar com outra conta
        </Button>
      </div>
    </>
  );
}
